// File: BigDataMap.tsx
"use client";

import React, { useEffect, useMemo, useState, useRef, FC, memo, useCallback } from "react";
import * as d3 from "d3";
import { GeoJsonLayer, ScatterplotLayer } from "@deck.gl/layers";
import { PickingInfo, LinearInterpolator } from "@deck.gl/core"; // Ensure WebMercatorViewport is imported if used in utils
import { GeoJsonProperties, Feature, FeatureCollection, Geometry, Polygon, MultiPolygon, BBox } from "geojson";
import * as topojson from "topojson-client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { throttle } from "lodash";
import { bbox } from "@turf/bbox"; // Ensure installed: npm install @turf/bbox
import { MjolnirGestureEvent } from "mjolnir.js";

// Import sub-components
import ClusterLegend from "@/components/maps/ClusterLegend";
import ModeSwitch from "@/components/maps/ModeSwitch";
import TooltipComponent from "@/components/maps/TooltipComponent";
import SelectionDetails from "@/components/maps/SelectionDetails";
import MapView from "@/components/maps/MapView";

// Import utility functions and types (Ensure paths are correct)
import {
  isSafeFeatureCollection,
  getFeatureId,
  calculatePointsInBoundary,
  calculateAreaCountsForCounty,
  // Assuming these two utils exist and are correctly defined as discussed:
  calculateBoundsViewState, // Utility to fit bounds (like initial US fit)
  getFitBoundsViewState, // Utility to fit points (like cluster fit)
} from "@/utils/BigDataMap.utils";
import {
  BigDataMapProps,
  SafeFeatureCollection,
  ViewStateType,
  SafeFeature,
  ExportMapData,
  UsTopoJSON,
  ClusterStats,
  ClusterCountsWithTotal, // Import the updated props type for verification
} from "@/types/BigDataMap.types";

// Define styles
const styles = `
  .stats-bar {
    padding: 10px;
    border-bottom: 1px solid #ccc;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    background: rgba(255, 255, 255, 0.8);
    z-index: 2;
    pointer-events: none;
    font-family: Arial, sans-serif;
  }
  .tooltip {
    position: absolute;
    z-index: 10;
    background: rgba(0,0,0,0.8);
    color: #fff;
    padding: 8px 12px;
    border-radius: 4px;
    pointer-events: none;
    font-size: 14px;
    transform: translate(-50%, -100%);
    white-space: nowrap;
    box-shadow: 0 0 5px rgba(0,0,0,0.3);
    transition: opacity 0.3s ease;
  }
  .tooltip.hidden {
    opacity: 0;
    visibility: hidden;
  }
  .tooltip.visible {
    opacity: 1;
    visibility: visible;
  }
  
  
`;

// --- Constants ---
const EXCLUDED_STATE_FIPS = ["02", "15", "72", "78", "69", "66", "60"]; // Alaska, Hawaii, PR, VI, MP, GU, AS
const COUNTIES_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json";

const PLACEHOLDER_INITIAL_VIEW_STATE: ViewStateType = {
  longitude: 0,
  latitude: 0,
  zoom: 1,
  minZoom: 3,
  maxZoom: 12,
  pitch: 0,
  bearing: 0,
};

// --- Component ---
const BigDataMap: FC<BigDataMapProps> = ({ data, clusterData }) => {
  // --- State ---
  const [combinedGeoJSON, setCombinedGeoJSON] = useState<SafeFeatureCollection<Geometry> | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; content: string } | null>(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [selectedAreaCounts, setSelectedAreaCounts] = useState<ClusterCountsWithTotal | null>(null);
  const [selectionMode, setSelectionMode] = useState<"region" | "cluster">("cluster");
  const [selectedCluster, setSelectedCluster] = useState<number | null>(null);
  const [selectedClusterStats, setSelectedClusterStats] = useState<ClusterStats | null>(null);
  const [filteredCluster, setFilteredCluster] = useState<number | null>(null);
  const [themeColors, setThemeColors] = useState<{
    selected: [number, number, number, number];
    default: [number, number, number, number];
    faded: [number, number, number, number];
  }>({
    selected: [0, 0, 0, 255], // Default Black (will be updated)
    default: [100, 100, 100, 255], // Default Gray (will be updated)
    faded: [200, 200, 200, 150], // Default Light Gray (will be updated)
  });
  const [isInitialViewCalculated, setIsInitialViewCalculated] = useState(false); // Track if calculation is done
  const [viewState, setViewState] = useState<ViewStateType>(PLACEHOLDER_INITIAL_VIEW_STATE);
  const [isResetting, setIsResetting] = useState(false);

  // --- Refs ---
  const mapContainerRef = useRef<HTMLDivElement>(null); // Ref for map container dimensions
  const calculatedInitialViewStateRef = useRef<ViewStateType | null>(null); // Ref for the *actual* initial state
  const hoverTimeout = useRef<number | null>(null);
  const hoveredClusterRef = useRef<number | null>(null);
  const hoveredFeatureIdRef = useRef<string | null>(null);
  const prevZoomRef = useRef<number>(PLACEHOLDER_INITIAL_VIEW_STATE.zoom);

  // --- Effects ---

  // Fetch and process GeoJSON data
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates on unmounted component
    fetch(COUNTIES_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch TopoJSON: ${response.statusText}`);
        }
        return response.json();
      })
      .then((topoData: UsTopoJSON) => {
        if (!isMounted) return; // Don't process if unmounted

        const countiesGeoRaw = topojson.feature(topoData, topoData.objects.counties) as FeatureCollection<
          Geometry,
          GeoJsonProperties
        >;
        const statesGeoRaw = topojson.feature(topoData, topoData.objects.states) as FeatureCollection<
          Geometry,
          GeoJsonProperties
        >;

        if (!isSafeFeatureCollection(countiesGeoRaw) || !isSafeFeatureCollection(statesGeoRaw)) {
          throw new Error("Expected FeatureCollection for counties and states");
        }

        const countiesGeo = countiesGeoRaw;
        const statesGeo = statesGeoRaw;

        const statesWithType = statesGeo.features.map(
          (feature: Feature<Geometry, GeoJsonProperties>): SafeFeature<Geometry> => ({
            ...feature,
            properties: { ...(feature.properties || {}), type: "state" },
          })
        );

        const countiesWithType = countiesGeo.features.map(
          (feature: Feature<Geometry, GeoJsonProperties>): SafeFeature<Geometry> => ({
            ...feature,
            properties: { ...(feature.properties || {}), type: "county" },
          })
        );

        const combinedFeatures: SafeFeature<Geometry>[] = [...statesWithType, ...countiesWithType];

        const combinedGeoJSONProcessed: SafeFeatureCollection<Geometry> = {
          type: "FeatureCollection",
          features: combinedFeatures,
        };

        setCombinedGeoJSON(combinedGeoJSONProcessed);
      })
      .catch((error) => {
        console.error("Error fetching or processing TopoJSON:", error);
        // Optionally set an error state here
      });

    return () => {
      isMounted = false; // Cleanup function to set flag on unmount
    };
  }, []); // Run only once on mount

  // Calculate initial view state based on GeoJSON bounds and container size
  useEffect(() => {
    // Check if already calculated or dependencies aren't ready
    if (isInitialViewCalculated || !combinedGeoJSON || !mapContainerRef.current) {
      // If dependencies aren't ready but calculation isn't done, log for debugging if needed
      // console.log("Initial view calc dependencies not ready or already calculated.", { isInitialViewCalculated, combinedGeoJSON: !!combinedGeoJSON, mapRef: !!mapContainerRef.current });
      return;
    }

    const mapElement = mapContainerRef.current;
    let calculationDone = false; // Local flag for ResizeObserver callback

    // --- Create ResizeObserver ---
    const resizeObserver = new ResizeObserver((entries) => {
      // Check if calculation was already triggered by this observer instance
      if (calculationDone) return;

      for (let entry of entries) {
        // Use contentRect for potentially more accurate dimensions during initial layout
        const { width, height } = entry.contentRect || {
          width: mapElement.offsetWidth,
          height: mapElement.offsetHeight,
        };

        console.log(`ResizeObserver triggered. Dimensions: W=${width}, H=${height}`);

        // --- Check for valid dimensions ---
        if (width > 0 && height > 0) {
          // --- Mark as done locally to prevent re-entry ---
          calculationDone = true;
          // --- Disconnect observer once we have dimensions ---
          // It's important to disconnect here so subsequent resizes (e.g., window resize)
          // don't re-trigger this initial calculation logic.
          resizeObserver.disconnect();
          console.log("ResizeObserver: Valid dimensions found, disconnecting and proceeding with calculation...");

          // --- Perform the initial view state calculation ---
          // Filter features for bounds calculation (continental US states)
          const continentalStatesFeatures = combinedGeoJSON.features.filter((feature) => {
            if (feature.properties?.type === "state") {
              const stateId = feature.id?.toString().padStart(2, "0");
              return stateId && !EXCLUDED_STATE_FIPS.includes(stateId);
            }
            return false;
          });

          if (continentalStatesFeatures.length > 0) {
            const continentalStatesCollection: FeatureCollection<Geometry> = {
              type: "FeatureCollection",
              features: continentalStatesFeatures,
            };

            try {
              const bounds = bbox(continentalStatesCollection); // Calculate bounds
              console.log(`GeoJSON Bounds for initial fit:`, bounds);

              // Validate calculated bounds before passing to viewport calculation
              if (
                !bounds ||
                bounds.length !== 4 ||
                !bounds.every(isFinite) ||
                bounds[0] >= bounds[2] ||
                bounds[1] >= bounds[3]
              ) {
                console.error("Initial View Calculation: Invalid GeoJSON bounds detected:", bounds);
                // Set fallback state and mark as done globally
                calculatedInitialViewStateRef.current = PLACEHOLDER_INITIAL_VIEW_STATE;
                setViewState(PLACEHOLDER_INITIAL_VIEW_STATE);
                setIsInitialViewCalculated(true); // Mark as done globally (even on error)
                return; // Stop processing in the callback
              }

              // Use calculateBoundsViewState (expects BBox, returns ViewStateType without transitions)
              const initialDynamicViewState = calculateBoundsViewState(
                bounds,
                width, // Use dimensions from ResizeObserver
                height, // Use dimensions from ResizeObserver
                {
                  padding: 40, // Example padding
                  // Pass min/max zoom from placeholder defaults if needed by util
                  minZoom: PLACEHOLDER_INITIAL_VIEW_STATE.minZoom,
                  maxZoom: PLACEHOLDER_INITIAL_VIEW_STATE.maxZoom,
                }
              );
              console.log("Calculated Initial View State:", initialDynamicViewState);

              // Validate the result before setting state
              if (
                initialDynamicViewState &&
                isFinite(initialDynamicViewState.longitude) &&
                isFinite(initialDynamicViewState.latitude) &&
                isFinite(initialDynamicViewState.zoom)
              ) {
                calculatedInitialViewStateRef.current = initialDynamicViewState; // Store for reset
                setViewState(initialDynamicViewState); // Update the main view state
                setIsInitialViewCalculated(true); // Mark calculation as done globally
              } else {
                console.error("Calculated initial view state is invalid:", initialDynamicViewState);
                calculatedInitialViewStateRef.current = PLACEHOLDER_INITIAL_VIEW_STATE;
                setViewState(PLACEHOLDER_INITIAL_VIEW_STATE);
                setIsInitialViewCalculated(true); // Mark globally as done (even on error)
              }
            } catch (error) {
              // Catch errors during bbox calculation or calculateBoundsViewState
              console.error("Error during initial view calculation process:", error);
              calculatedInitialViewStateRef.current = PLACEHOLDER_INITIAL_VIEW_STATE;
              setViewState(PLACEHOLDER_INITIAL_VIEW_STATE);
              setIsInitialViewCalculated(true); // Mark globally as done (even on error)
            }
          } else {
            // Handle case where no continental states were found after filtering
            console.warn("Initial View Calculation: No continental US states found. Using placeholder.");
            calculatedInitialViewStateRef.current = PLACEHOLDER_INITIAL_VIEW_STATE;
            setViewState(PLACEHOLDER_INITIAL_VIEW_STATE);
            setIsInitialViewCalculated(true); // Mark globally as done
          }
          // --- End of calculation logic ---
        } else {
          // Dimensions are still zero or invalid, observer will likely fire again
          console.log("ResizeObserver: Dimensions still zero or invalid, waiting...");
        }
      } // End for loop (usually only one entry)
    }); // End ResizeObserver callback

    // --- Start observing the map element ---
    console.log("Starting ResizeObserver for initial dimensions.");
    resizeObserver.observe(mapElement);

    // --- Cleanup function for the useEffect ---
    return () => {
      // Disconnect the observer when the component unmounts or dependencies change
      console.log("Disconnecting ResizeObserver due to cleanup.");
      resizeObserver.disconnect();
    };

    // Dependencies: Effect should re-run if the data loads *after* the initial mount,
    // or if the `isInitialViewCalculated` flag changes (though it only goes false -> true).
    // The ref changing doesn't trigger re-runs, which is correct.
  }, [isInitialViewCalculated, combinedGeoJSON]);

  // Get Theme Colors from CSS Variables (Client-side only)
  useEffect(() => {
    const getCssVarColor = (cssVar: string): [number, number, number, number] => {
      if (typeof window === "undefined") return [0, 0, 0, 255]; // Default for SSR

      const hsl = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
      if (!hsl) return [0, 0, 0, 255]; // Fallback if var not found

      // Use a temporary element to convert HSL to RGB
      const temp = document.createElement("div");
      temp.style.color = `hsl(${hsl})`;
      temp.style.display = "none"; // Hide element
      document.body.appendChild(temp);
      const rgbString = window.getComputedStyle(temp).color;
      document.body.removeChild(temp);

      const rgbMatch = rgbString.match(/\d+/g);
      const rgb = rgbMatch ? rgbMatch.map(Number) : [0, 0, 0];
      return [...rgb, 255] as [number, number, number, number];
    };

    setThemeColors({
      selected: getCssVarColor("--primary"),
      default: getCssVarColor("--secondary-foreground"),
      // Use a different alpha for faded or adjust as needed
      faded: [...getCssVarColor("--muted-foreground").slice(0, 3), 150] as [number, number, number, number],
    });
  }, []); // Run once on mount

  // Inject component-specific styles (optional alternative to global CSS)
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
    return () => {
      // Check if the stylesheet is still in the head before removing
      if (styleSheet.parentNode === document.head) {
        document.head.removeChild(styleSheet);
      }
    };
  }, []); // Run once on mount

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout.current) {
        window.clearTimeout(hoverTimeout.current);
      }
    };
  }, []);

  // Effect to monitor zoom changes and potentially deselect state
  useEffect(() => {
    const prevZoom = prevZoomRef.current;
    const currentZoom = viewState.zoom;
    const zoomThreshold = 5; // Zoom level to show counties

    if (prevZoom < zoomThreshold && currentZoom >= zoomThreshold) {
      if (selectedFeatureId && selectedFeatureId.startsWith("state-")) {
        // console.log("Clearing state selection due to zoom.");
        // Only clear state-related selections, not cluster selections
        setSelectedFeatureId(null);
        setSelectedAreaCounts(null);
        // Keep tooltip handling within hover logic to avoid clearing unnecessarily
        // setTooltip(null);
      }
    }

    // Update previous zoom for the next comparison
    prevZoomRef.current = currentZoom;
  }, [viewState.zoom, selectedFeatureId]); // Depend on zoom and selected feature

  // --- Memoized Calculations ---

  // Color scale based on data clusters
  const colorScale = useMemo(() => {
    if (!data || data.length === 0) return d3.scaleSequential(d3.interpolateRainbow).domain([0, 1]);
    const clusterValues = data.map((d) => d.clusters).filter((v) => typeof v === "number");
    if (clusterValues.length === 0) return d3.scaleSequential(d3.interpolateRainbow).domain([0, 1]);
    const [minCluster, maxCluster] = d3.extent(clusterValues) as [number, number];
    // Handle case where min and max are the same (only one cluster value)
    return d3
      .scaleSequential(d3.interpolateRainbow)
      .domain([minCluster, maxCluster === minCluster ? minCluster + 1 : maxCluster]);
  }, [data]);

  // Map cluster numbers to RGBA colors
  const clusterColors = useMemo(() => {
    const uniqueClusters = Array.from(new Set(data?.map((d) => d.clusters).filter((v) => typeof v === "number") ?? []));
    const colorMap: { [key: number]: [number, number, number, number] } = {};

    uniqueClusters.forEach((cluster) => {
      const colorObj = d3.color(colorScale(cluster));
      if (colorObj instanceof d3.rgb) {
        colorMap[cluster] = [colorObj.r, colorObj.g, colorObj.b, 200]; // Default alpha
      } else {
        colorMap[cluster] = [128, 128, 128, 200]; // Default gray if color conversion fails
      }
    });
    return colorMap;
  }, [data, colorScale]);

  // --- Callbacks ---

  // Calculate statistics for a given cluster number
  const calculateClusterStats = useCallback(
    (clusterNum: number): ClusterStats | null => {
      if (!data || !clusterData || !combinedGeoJSON) return null; // Need all data sources

      const clusterPoints = data.filter((d) => d.clusters === clusterNum);
      if (clusterPoints.length === 0) return { pointCount: 0, avgR2: null, avgMSE: null, regionCounts: {} };

      // Calculate averages from clusterData
      const relevantClusterData = clusterData.filter((d) => d.cluster === clusterNum);
      const avgR2 =
        relevantClusterData.length > 0
          ? relevantClusterData.reduce((sum, d) => sum + (d.r2 ?? 0), 0) / relevantClusterData.length
          : null;
      const avgMSE =
        relevantClusterData.length > 0
          ? relevantClusterData.reduce((sum, d) => sum + (d.mse ?? 0), 0) / relevantClusterData.length
          : null;

      // Calculate region counts (e.g., points per state)
      const regionCounts: { [key: string]: number } = {};
      combinedGeoJSON.features.forEach((feature) => {
        if (feature.properties?.type === "state") {
          // Only count for states in this example
          const pointsInState = calculatePointsInBoundary(feature, clusterPoints);
          if (pointsInState && pointsInState.totalPoints > 0) {
            regionCounts[feature.properties.name || `State ID ${feature.id}`] = pointsInState.totalPoints;
          }
        }
      });

      return {
        pointCount: clusterPoints.length,
        avgR2,
        avgMSE,
        regionCounts,
      };
    },
    [data, clusterData, combinedGeoJSON] // Dependencies: all data sources
  );

  // Reset map to initial calculated view and clear selections
  const resetView = useCallback(() => {
    // Set the resetting flag
    setIsResetting(true);

    const targetViewState = calculatedInitialViewStateRef.current || PLACEHOLDER_INITIAL_VIEW_STATE;
    setViewState({
      longitude: targetViewState.longitude,
      latitude: targetViewState.latitude,
      zoom: targetViewState.zoom,
      pitch: targetViewState.pitch ?? 0,
      bearing: targetViewState.bearing ?? 0,
      minZoom: targetViewState.minZoom,
      maxZoom: targetViewState.maxZoom,
      // REMOVED transitionDuration and transitionInterpolator from here
    });
    // Clear all selection/filter states
    setFilteredCluster(null);
    setSelectedCluster(null);
    setSelectedClusterStats(null);
    setSelectedFeatureId(null);
    setSelectedAreaCounts(null);
    setTooltip(null); // Clear tooltip
    setSelectionMode("cluster"); // Optionally reset mode, or keep current? Resetting seems safer.
    // Reset hover refs to prevent sticky hovers after reset
    hoveredClusterRef.current = null;
    hoveredFeatureIdRef.current = null;
    console.log("Map view reset.");

    // Clear the resetting flag after the transition duration
    setTimeout(() => {
      setIsResetting(false);
    }, 500); // Match this with the transition duration used in MapView
  }, []); // No dependencies as it uses refs and setters only

  // handleClusterZoomRequest - MODIFIED: Use corrected getFitBoundsViewState
  const handleClusterZoomRequest = useCallback(
    (clusterNum: number) => {
      const mapDiv = mapContainerRef.current;
      if (!mapDiv || !data) return; // Guard clauses

      const clusterPoints = data.filter((d) => d.clusters === clusterNum);

      if (clusterPoints.length > 0) {
        const { offsetWidth, offsetHeight } = mapDiv;
        if (offsetWidth > 0 && offsetHeight > 0) {
          // Use the CORRECTED getFitBoundsViewState (takes dimensions, returns NO transitions)
          const newViewState = getFitBoundsViewState(
            clusterPoints,
            offsetWidth,
            offsetHeight,
            { padding: 60, minZoom: 4, maxZoom: 16 } // Customize options
          );
          setViewState(newViewState); // Update the main view state (no transitions here)
        }
      }
    },
    [data, setViewState]
  );

  // Throttle hover handlers to improve performance
  const handleScatterplotHover = useCallback(
    throttle((pickingInfo: PickingInfo) => {
      if (selectionMode !== "cluster") {
        // Only react in cluster mode
        // Clear tooltip if switching modes while hovering
        if (tooltip) setTooltip(null);
        return;
      }

      const newHoveredCluster = pickingInfo.object ? (pickingInfo.object as ExportMapData).clusters : null;
      hoveredClusterRef.current = newHoveredCluster; // Update ref directly

      if (pickingInfo.object) {
        setTooltip({
          x: pickingInfo.x,
          y: pickingInfo.y,
          content: `Cluster ${(pickingInfo.object as ExportMapData).clusters}`,
        });
      } else {
        setTooltip(null); // Clear tooltip if hovering off points
      }
    }, 100), // Throttle interval
    [selectionMode, tooltip] // Depend on mode to enable/disable, tooltip to potentially clear it
  );

  const handleCountyHover = useCallback(
    throttle((pickingInfo: PickingInfo) => {
      const zoomThreshold = 5;
      if (selectionMode !== "region" || viewState.zoom < zoomThreshold) {
        hoveredFeatureIdRef.current = null; // Clear hover ref
        if (tooltip) setTooltip(null); // Clear tooltip if disabled by mode/zoom
        return;
      }

      const { x, y, object } = pickingInfo;
      const feature = object as SafeFeature<Geometry> | null;

      if (feature && feature.properties?.type === "county") {
        const featureId = getFeatureId(feature);
        const countyName = feature.properties?.name;

        if (hoveredFeatureIdRef.current !== featureId) {
          // Update only on change
          hoveredFeatureIdRef.current = featureId;
          setTooltip({ x, y, content: `${countyName || "Unknown"} County` });
        }
      } else if (hoveredFeatureIdRef.current !== null) {
        // Clear if moved off a county
        hoveredFeatureIdRef.current = null;
        setTooltip(null);
      }
    }, 100),
    [selectionMode, viewState.zoom, tooltip] // Depend on mode, zoom, and tooltip state
  );

  const handleStateHover = useCallback(
    throttle((pickingInfo: PickingInfo) => {
      if (selectionMode !== "region") {
        hoveredFeatureIdRef.current = null;
        if (tooltip) setTooltip(null);
        return;
      }

      const { x, y, object } = pickingInfo;
      const feature = object as SafeFeature<Geometry> | null;

      if (feature && feature.properties?.type === "state") {
        const featureId = getFeatureId(feature);
        const stateName = feature.properties?.name;

        if (hoveredFeatureIdRef.current !== featureId) {
          hoveredFeatureIdRef.current = featureId;
          setTooltip({ x, y, content: `${stateName || "Unknown State"}` });
        }
      } else if (hoveredFeatureIdRef.current !== null) {
        // Clear only if we were previously hovering something
        hoveredFeatureIdRef.current = null;
        setTooltip(null);
      }
    }, 100),
    [selectionMode, tooltip] // Depend on mode and tooltip state
  );

  // Click handler for regions (states/counties)
  const handleRegionClick = useCallback(
    (info: PickingInfo) => {
      if (selectionMode !== "region") return; // Only active in region mode

      const object = info.object as SafeFeature<Geometry, GeoJsonProperties> | null;
      if (object) {
        const featureId = getFeatureId(object);
        const regionType = object.properties?.type; // "state" or "county"
        const newSelectedFeatureId = `${regionType}-${featureId}`;

        // Deselect if clicking the same feature again
        if (newSelectedFeatureId === selectedFeatureId) {
          resetView(); // Use resetView for consistent clearing/resetting
          // console.log(`${regionType} deselected: ${newSelectedFeatureId}`);
          return;
        }

        // Select new feature
        // console.log(`Region selected: ${newSelectedFeatureId}`);
        setSelectedFeatureId(newSelectedFeatureId);

        // Clear cluster selection state
        setSelectedCluster(null);
        setSelectedClusterStats(null);
        setFilteredCluster(null); // Clear filter as well

        // Calculate area counts for the selected feature
        if (object.geometry && (object.geometry.type === "Polygon" || object.geometry.type === "MultiPolygon")) {
          // Ensure data is available for calculation
          if (data) {
            const areaCounts = calculatePointsInBoundary(object, data);
            setSelectedAreaCounts(areaCounts);
            // console.log(`Area counts for ${newSelectedFeatureId}:`, areaCounts);
          } else {
            console.warn("Cannot calculate area counts: data not available.");
            setSelectedAreaCounts(null);
          }
        } else {
          console.error(`Region ${newSelectedFeatureId} does not have a valid Polygon or MultiPolygon geometry.`);
          setSelectedAreaCounts(null);
        }

        if (data) {
          const areaCounts = calculatePointsInBoundary(object, data);
          setSelectedAreaCounts(areaCounts);
        }
        setTooltip(null); // Clear tooltip after click
      }
    },
    [
      selectionMode,
      selectedFeatureId,
      resetView,
      data,
      setSelectedFeatureId,
      setSelectedAreaCounts,
      setSelectedCluster,
      setSelectedClusterStats,
      setFilteredCluster,
    ] // Include all state setters and data
  );

  // Click handler for the map canvas background - Uses the imported type
  const handleCanvasClick = useCallback(
    (info: PickingInfo, event: MjolnirGestureEvent) => {
      // <-- Use type from mjolnir.js
      if (!info.object && !info.layer) {
        resetView();
      }
    },
    [resetView]
  );

  // --- Layer Definitions ---

  // Memoized GeoJSON Layers (States and Counties)
  const geoJsonLayers = useMemo(() => {
    if (!combinedGeoJSON) return []; // Return empty array if data not ready

    const { selected: selectedColorRGB, default: defaultColorRGB, faded: fadedColorRGB } = themeColors;
    const zoomThreshold = 5; // Zoom level to switch county visibility/styling

    // Filter out excluded geographies (e.g., AK, HI)
    const filteredFeatures = combinedGeoJSON.features.filter((feature) => {
      const properties = feature.properties;
      if (!properties) return true; // Keep features without properties? Decide based on data.

      let stateFips: string | null = null;
      if (properties.type === "state") {
        stateFips = feature.id?.toString().padStart(2, "0") ?? null;
      } else if (properties.type === "county") {
        const countyId = feature.id?.toString();
        if (countyId && countyId.length >= 2) {
          stateFips = countyId.slice(0, 2);
        }
      }
      return stateFips ? !EXCLUDED_STATE_FIPS.includes(stateFips) : true;
    });

    const stateFeatures = filteredFeatures.filter((f) => f.properties?.type === "state");
    const countyFeatures = filteredFeatures.filter((f) => f.properties?.type === "county");

    // County Layer Definition
    const countyLayer = new GeoJsonLayer<Geometry, any>({
      id: "county-layer",
      data: { type: "FeatureCollection", features: countyFeatures },
      visible: viewState.zoom >= zoomThreshold, // Only visible when zoomed in
      pickable: selectionMode === "region",
      autoHighlight: selectionMode === "region",
      highlightColor: [0, 0, 0, 50], // Semi-transparent black highlight
      filled: true, // Keep filled for picking, but transparent
      stroked: true, // Show borders
      getFillColor: [0, 0, 0, 0], // Transparent fill
      getLineColor: (f: SafeFeature<Geometry>) => {
        const id = `${f.properties?.type}-${getFeatureId(f)}`;
        return id === selectedFeatureId ? selectedColorRGB : fadedColorRGB; // Highlight selected county border
      },
      lineWidthUnits: "pixels",
      getLineWidth: (f: SafeFeature<Geometry>) => {
        const id = `${f.properties?.type}-${getFeatureId(f)}`;
        return id === selectedFeatureId ? 2 : 0.5; // Thicker border for selected county
      },
      lineWidthMinPixels: 0.5,
      lineWidthMaxPixels: 3,
      onClick: handleRegionClick, // Use the memoized handler
      onHover: handleCountyHover, // Use the memoized handler
    });

    // State Layer Definition
    const stateLayer = new GeoJsonLayer<Geometry, any>({
      id: "state-layer",
      data: { type: "FeatureCollection", features: stateFeatures },
      visible: true, // Always visible
      pickable: selectionMode === "region",
      autoHighlight: selectionMode === "region",
      highlightColor: [0, 0, 0, 30], // Lighter highlight for states
      filled: true, // Keep filled for picking and slight visual cue
      stroked: true,
      getFillColor: (f: SafeFeature<Geometry>) => {
        const id = `${f.properties?.type}-${getFeatureId(f)}`;
        // Slightly shade selected state or maybe based on hover? Example: subtle fill for selected.
        return id === selectedFeatureId
          ? [selectedColorRGB[0], selectedColorRGB[1], selectedColorRGB[2], 15]
          : [0, 0, 0, 0];
      },
      getLineColor: (f: SafeFeature<Geometry>) => {
        const id = `${f.properties?.type}-${getFeatureId(f)}`;
        // Use default color for state lines, highlight selected state border
        return id === selectedFeatureId ? selectedColorRGB : defaultColorRGB;
      },
      lineWidthUnits: "pixels",
      getLineWidth: (f: SafeFeature<Geometry>) => {
        const id = `${f.properties?.type}-${getFeatureId(f)}`;
        // Make selected state border thicker, adjust based on zoom
        const baseWidth =
          id === selectedFeatureId
            ? viewState.zoom >= zoomThreshold
              ? 2
              : 1.5
            : viewState.zoom >= zoomThreshold
              ? 0.75
              : 0.5;
        return baseWidth;
      },
      lineWidthMinPixels: 0.5,
      lineWidthMaxPixels: 4,
      onClick: handleRegionClick, // Use the memoized handler
      onHover: handleStateHover, // Use the memoized handler
    });

    return [stateLayer, countyLayer];
  }, [
    combinedGeoJSON,
    viewState.zoom,
    selectionMode,
    themeColors,
    selectedFeatureId,
    handleRegionClick,
    handleCountyHover,
    handleStateHover,
  ]);

  // Memoized Scatterplot Layer for Data Points
  const scatterplotLayer = useMemo(() => {
    if (!data) return null; // Need data

    // Filter data based on the filteredCluster state (from legend)
    const filteredPointData = filteredCluster !== null ? data.filter((d) => d.clusters === filteredCluster) : data;
    if (filteredPointData.length === 0 && data.length > 0) {
      // Handle case where filter results in no points (optional: show message or hide layer)
      console.log(`No points found for filtered cluster: ${filteredCluster}`);
    }

    const baseRadius = 4; // Base size of points

    return new ScatterplotLayer<ExportMapData>({
      id: "points-layer",
      data: filteredPointData,
      getPosition: (d) => [d.longitude, d.latitude],
      pickable: true, // Allow picking points for cluster selection/hover
      autoHighlight: false, // Use custom hover logic via onHover and refs
      opacity: 0.8, // Overall layer opacity

      // Radius depends on selection mode, hover state, and selection state
      radiusUnits: "pixels",
      radiusScale: 1, // Adjust if needed
      radiusMinPixels: 1.5,
      radiusMaxPixels: 10,
      getRadius: (d) => {
        if (selectionMode !== "cluster") return baseRadius * 0.8; // Slightly smaller if not in cluster mode

        const isHovered = hoveredClusterRef.current === d.clusters;
        const isSelected = selectedCluster === d.clusters;

        if (isSelected) return baseRadius * 1.5; // Emphasize selected
        if (isHovered) return baseRadius * 1.3; // Emphasize hovered

        // If *any* cluster is selected or hovered, fade out non-matching points slightly
        if ((selectedCluster !== null || hoveredClusterRef.current !== null) && !isSelected && !isHovered) {
          return baseRadius * 0.6;
        }

        return baseRadius; // Default size
      },

      // Fill color depends on cluster color and selection/hover state
      getFillColor: (d) => {
        const color = clusterColors[d.clusters] || [128, 128, 128, 150]; // Use default gray if color missing
        const baseAlpha = color[3]; // Original alpha from clusterColors

        if (selectionMode !== "cluster") {
          // Muted appearance when not in cluster mode
          return [color[0], color[1], color[2], baseAlpha * 0.5];
        }

        const isHovered = hoveredClusterRef.current === d.clusters;
        const isSelected = selectedCluster === d.clusters;

        if (isSelected) return [color[0], color[1], color[2], 255]; // Fully opaque selected
        if (isHovered) return [color[0], color[1], color[2], 240]; // Almost opaque hovered

        // If *any* cluster is selected or hovered, fade out non-matching points more
        if ((selectedCluster !== null || hoveredClusterRef.current !== null) && !isSelected && !isHovered) {
          return [color[0], color[1], color[2], baseAlpha * 0.1]; // Significantly faded
        }

        return color; // Default color/alpha
      },

      // Optional: Add stroke for selected cluster points
      stroked: false,
      lineWidthUnits: "pixels",
      lineWidthMinPixels: 0,
      lineWidthMaxPixels: 2,
      getLineWidth: (d) => (selectionMode === "cluster" && selectedCluster === d.clusters ? 1 : 0),
      getLineColor: [0, 0, 0, 200], // Black stroke for selected

      // Define update triggers for Deck.gl optimization
      updateTriggers: {
        getRadius: [selectionMode, hoveredClusterRef.current, selectedCluster],
        getFillColor: [selectionMode, hoveredClusterRef.current, selectedCluster, clusterColors],
        getLineWidth: [selectionMode, selectedCluster],
        // Data filtering changes when filteredCluster changes
        data: [filteredCluster],
      },

      // Define transitions for smooth visual changes
      transitions: {
        getRadius: { duration: 100, easing: d3.easeCubicInOut },
        getFillColor: { duration: 100, easing: d3.easeCubicInOut },
        getLineWidth: { duration: 100, easing: d3.easeCubicInOut },
      },

      // Click handler for selecting clusters directly on the map
      onClick: (pickingInfo: PickingInfo) => {
        if (selectionMode === "cluster" && pickingInfo.object) {
          const clickedCluster = (pickingInfo.object as ExportMapData).clusters;

          if (clickedCluster === selectedCluster) {
            resetView(); // Deselect: OK
            return;
          }

          // --- Select new cluster via map click ---
          setSelectedCluster(clickedCluster);
          setFilteredCluster(clickedCluster);
          setSelectedFeatureId(null);
          setSelectedAreaCounts(null);
          const stats = calculateClusterStats(clickedCluster);
          setSelectedClusterStats(stats);

          // 3. Calculate and set view state to zoom to the cluster
          const mapDiv = mapContainerRef.current;
          if (mapDiv && data) {
            const clusterPoints = data.filter((d) => d.clusters === clickedCluster);
            if (clusterPoints.length > 0) {
              const { offsetWidth, offsetHeight } = mapDiv;
              if (offsetWidth > 0 && offsetHeight > 0) {
                const newViewState = getFitBoundsViewState(clusterPoints, offsetWidth, offsetHeight, {
                  padding: 60,
                  minZoom: 4,
                  maxZoom: 16,
                });
                setViewState(newViewState);
              } else {
                console.warn("Map has no dimensions, cannot zoom to cluster.");
                // Maybe do not zoom, just select? Or reset as fallback?
                // resetView();
              }
            } else {
              console.warn(`No points found for selected cluster ${clickedCluster}, cannot zoom.`);
              // resetView(); // Reset if no points found?
            }
          } else {
            console.warn("Map container or data not ready, cannot zoom to cluster.");
            // resetView(); // Reset if map/data not ready?
          }
          // --- End select new cluster ---
        }
      },
      // Use the memoized hover handler
      onHover: handleScatterplotHover,
    });
  }, [
    data, // Main data source
    filteredCluster, // For filtering data
    selectionMode,
    selectedCluster, // For styling selected points
    clusterColors, // For point colors
    themeColors, // Potentially used if styling depends on theme beyond clusterColors
    resetView, // For deselect click
    calculateClusterStats, // For calculating stats on select
    setViewState, // For zooming on select
    setSelectedCluster, // State setters used in onClick
    setFilteredCluster,
    setSelectedFeatureId,
    setSelectedAreaCounts,
    setSelectedClusterStats,
    handleScatterplotHover, // Hover handler
    // hoveredClusterRef.current cannot be a dependency, but it's used internally and triggers are set
  ]);

  // Combine all layers for rendering
  const layers = useMemo(() => {
    const activeLayers = [];
    if (geoJsonLayers.length > 0) {
      activeLayers.push(...geoJsonLayers);
    }
    if (scatterplotLayer) {
      activeLayers.push(scatterplotLayer);
    }
    // console.log("Composing layers. GeoJSON:", geoJsonLayers.length, "Scatterplot:", !!scatterplotLayer);
    return activeLayers;
  }, [geoJsonLayers, scatterplotLayer]);

  // --- Render ---
  return (
    <Card className="bg-card text-card-foreground shadow-md z-10 w-full flex flex-col h-[75vh]">
      {" "}
      {/* Use theme colors, flex column */}
      <CardHeader className="pb-0 flex-shrink-0">
        {" "}
        {/* Prevent header shrinking */}
        <div className="flex justify-between items-start">
          {" "}
          {/* Align title and switch */}
          <div>
            <CardTitle className="text-lg">Geographic Cluster Distribution</CardTitle>
            <CardDescription>Interact by selecting regions or clusters.</CardDescription>
          </div>
          <ModeSwitch
            selectionMode={selectionMode}
            setSelectionMode={setSelectionMode}
            setSelectedFeatureId={setSelectedFeatureId}
            setSelectedAreaCounts={setSelectedAreaCounts}
            setSelectedCluster={setSelectedCluster}
            setSelectedClusterStats={setSelectedClusterStats}
            resetView={resetView} // Pass the unified resetView
          />
        </div>
      </CardHeader>
      {/* Make CardContent grow to fill available space */}
      <CardContent className="flex-grow flex flex-col gap-4 pt-4 overflow-hidden h-full">
        {/* Allow content to grow, hide overflow */}
        {/* Cluster Legend (conditionally rendered if data exists?) */}
        {data && data.length > 0 && (
          <ClusterLegend
            clusterColors={clusterColors}
            filteredCluster={filteredCluster}
            setFilteredCluster={setFilteredCluster}
            data={data} // Still passing data for stats calc within legend
            resetView={resetView}
            calculateClusterStats={calculateClusterStats} // Pass the function
            setSelectedCluster={setSelectedCluster}
            setSelectedClusterStats={setSelectedClusterStats}
            setSelectionMode={setSelectionMode}
            onClusterZoomRequest={handleClusterZoomRequest} // Pass the new handler
          />
        )}
        {/* Map Container - Make it grow */}
        <div
          ref={mapContainerRef}
          className="map-container bg-muted/30 dark:bg-muted/10 border border-border rounded-xl overflow-hidden relative w-full flex-grow " // Use flex-grow
        >
          {/* Loading Indicator */}
          {!isInitialViewCalculated && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20 text-foreground">
              <p>Loading Map Data...</p> {/* Basic Loading Indicator */}
            </div>
          )}

          {/* Map View (Render only when initial view is calculated and GeoJSON exists) */}
          {isInitialViewCalculated && combinedGeoJSON && (
            <MapView
              viewState={viewState} // Controlled state
              setViewState={setViewState} // Allow map interaction
              layers={layers}
              handleCanvasClick={handleCanvasClick}
              // --- Pass transition props here ---
              transitionDuration={0} // Default duration for view changes
              transitionInterpolator={new LinearInterpolator()} // Use Deck.gl's interpolator
              // You could make duration/interpolator dynamic if needed
              isResetting={isResetting}
            />
          )}

          {/* Tooltip Overlay */}
          <TooltipComponent tooltip={tooltip} />

          {/* Selection Details Overlay (Positioned absolutely or outside map) */}
          <SelectionDetails
            selectionMode={selectionMode}
            selectedFeatureId={selectedFeatureId}
            selectedAreaCounts={selectedAreaCounts}
            clusterColors={clusterColors}
            combinedGeoJSON={combinedGeoJSON}
            selectedCluster={selectedCluster}
            selectedClusterStats={selectedClusterStats}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default memo(BigDataMap);
